import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatErr } from "@/lib/api";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t("auth.invalidResetLink"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, ...form });
      setDone(true);
      toast.success(t("auth.passwordResetToast"));
    } catch (err) {
      toast.error(formatErr(err, t("auth.invalidExpiredLink")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7] grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-3 mb-8" data-testid="reset-brand">
          <img src="/edujac-mark.png" alt="EDUJAC" className="h-16 w-16 object-contain" />
          <span className="text-center">
            <span className="block font-heading font-extrabold text-[26px] leading-none tracking-wide text-[#00194E]">EDUJAC</span>
            <span className="block text-[11px] font-semibold tracking-[0.08em] text-[#119687] mt-1.5">{t("auth.platformSubtitle")}</span>
          </span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <h1 className="font-heading text-2xl font-bold">{t("auth.passwordUpdated")}</h1>
              <p className="text-sm text-slate-500 mt-3">{t("auth.passwordUpdatedDescription")}</p>
              <Button onClick={() => navigate("/login")} data-testid="reset-goto-login" className="mt-6 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8">
                {t("auth.goToLogin")}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              <h1 className="font-heading text-2xl font-bold">{t("auth.setNewPassword")}</h1>
              <p className="text-sm text-slate-500 mt-1">{t("auth.setNewPasswordDescription")}</p>
              <div className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="reset-new">{t("auth.newPassword")}</Label>
                  <Input id="reset-new" data-testid="reset-new" type="password" required autoComplete="new-password" className="mt-1.5" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
                  <p className="text-[11px] text-slate-400 mt-1">{t("auth.passwordHint")}</p>
                </div>
                <div>
                  <Label htmlFor="reset-confirm">{t("auth.confirmNewPassword")}</Label>
                  <Input id="reset-confirm" data-testid="reset-confirm" type="password" required autoComplete="new-password" className="mt-1.5" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} />
                </div>
                <Button type="submit" data-testid="reset-submit" disabled={busy} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold py-5 transition-transform duration-300 active:scale-95">
                  {busy ? t("auth.updating") : t("auth.resetPassword")}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
